// In-app forum notifications bell.
//
// Mounted in the top-right of the app shell (fixed). Polls the unread count
// every 30 seconds. Click → opens a dropdown of the most recent 20
// notifications. Clicking a row marks-as-read + navigates to the post.
//
// We re-use the existing `af:forum-changed` event broadcast in AppShell so
// any forum mutation in the app instantly refreshes the bell.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check, Trophy, AtSign, MessageCircle, MessagesSquare } from "lucide-react";
import { timeAgo } from "@/lib/forumCategories";

const TYPE_META = {
  reply_to_my_post:    { icon: MessageCircle,   verb: "replied to your question" },
  reply_to_my_reply:   { icon: MessagesSquare,  verb: "replied to your comment" },
  best_answer_chosen:  { icon: Trophy,          verb: "marked your reply as the best answer" },
  mentioned:           { icon: AtSign,          verb: "mentioned you" },
};

export default function NotificationsBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/forum/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* swallow */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forum/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + poll + event-driven refresh.
  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 30_000);
    const onForumChange = () => loadCount();
    window.addEventListener("af:forum-changed", onForumChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("af:forum-changed", onForumChange);
    };
  }, [loadCount]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) loadItems();
      return next;
    });
  };

  const clickNotif = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await api.post(`/forum/notifications/${n.id}/read`); } catch { /* ignore */ }
      loadCount();
    }
    if (n.post_id) {
      const path = `/app/community/forum/${n.post_id}` + (n.reply_id ? `#reply-${n.reply_id}` : "");
      nav(path);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/forum/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setCount(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed top-3 right-4 z-40" ref={ref} data-testid="notifications-bell-root">
      <button
        type="button"
        onClick={toggle}
        className="relative w-9 h-9 rounded-full bg-white border border-zinc-200 shadow-sm hover:border-zinc-400 flex items-center justify-center text-zinc-700"
        title="Notifications"
        aria-label={count > 0 ? `Notifications (${count} unread)` : "Notifications"}
        data-testid="notifications-bell-btn"
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center px-1"
            data-testid="notifications-unread-badge"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-11 right-0 w-[360px] max-h-[480px] overflow-hidden bg-white border border-zinc-200 rounded-lg shadow-xl flex flex-col"
          data-testid="notifications-dropdown"
        >
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <div className="text-sm font-semibold text-zinc-900">Notifications</div>
            {items.some((n) => !n.is_read) && (
              <button
                type="button"
                onClick={markAll}
                className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
                data-testid="notifications-mark-all-read"
              >
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="px-3 py-6 text-xs text-zinc-500 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-8 text-xs text-zinc-500 text-center" data-testid="notifications-empty">
                No notifications yet.
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || { icon: Bell, verb: "did something" };
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => clickNotif(n)}
                    className={
                      "w-full text-left px-3 py-2.5 flex items-start gap-2.5 border-b border-zinc-100 hover:bg-zinc-50 transition " +
                      (n.is_read ? "" : "bg-emerald-50/50")
                    }
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={
                      "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 " +
                      (n.is_read ? "bg-zinc-100 text-zinc-500" : "bg-emerald-100 text-emerald-700")
                    }>
                      <Icon size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-700 leading-snug">
                        <span className="font-semibold">{n.actor?.display_name || "Someone"}</span>{" "}
                        {meta.verb}
                      </div>
                      {n.post_title && (
                        <div className="text-[11px] text-zinc-500 truncate mt-0.5">re: {n.post_title}</div>
                      )}
                      {n.snippet && (
                        <div className="text-[11px] text-zinc-400 truncate mt-0.5 italic">&ldquo;{n.snippet}&rdquo;</div>
                      )}
                      <div className="text-[10px] text-zinc-400 mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
